import React, { useState, useEffect, useCallback } from 'react'
import { 
  Modal, 
  Card, 
  Form, 
  FormLayout, 
  TextField, 
  Stack, 
  Badge, 
  Toast,
  DataTable,
  Caption,
  TextStyle,
  Spinner,
  Link,
  Pagination
} from '@shopify/polaris'
import { createApi } from '../api'
import { getTranslation } from '../util/getTranslation'

interface CSVExportDialogProps {
  open: boolean
  onClose: () => void
}

interface ExportStatus {
  id: string
  accountId: string
  status: 'processing' | 'completed' | 'failed'
  createdAt: string
  completedAt?: string
  downloadUrl?: string

  error?: string
  totalRecords?: number
}

export function CSVExportDialog({ open, onClose }: CSVExportDialogProps) {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [exports, setExports] = useState<ExportStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [toastMessage, setToastMessage] = useState('')
  const [toastError, setToastError] = useState(false)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10) // Items per page constant

  const api = createApi()

  const loadExports = useCallback(async () => {
    try {
      const response = await api.listExports()
      setExports(response.exports)
    } catch (error) {
      console.error('Failed to load exports:', error)
    } finally {
      setLoading(false)
    }
  }, [api])

  // Load existing exports when dialog opens
  useEffect(() => {
    if (open) {
      loadExports()
      // Set default date range (last 30 days)
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 30)
      
      setEndDate(end.toISOString().split('T')[0])
      setStartDate(start.toISOString().split('T')[0])
      // Reset pagination when dialog opens
      setCurrentPage(1)
    }
  }, [open])

  useEffect(() => {
    if (!open) return;

    const hasProcessing = exports.some(exp => exp.status === 'processing')
    if (!hasProcessing) return;

    const intervalId = setInterval(() => {
      void loadExports();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [open, exports, loadExports]);

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setToastMessage(getTranslation('gpwebpay.start_date_required'))
      setToastError(true)
      return
    }

    const start = new Date(startDate)
    const end = new Date(endDate)

    if (start >= end) {
      setToastMessage(getTranslation('gpwebpay.start_before_end'))
      setToastError(true)
      return
    }

    setIsExporting(true)
    try {
      const response = await api.exportPayments(
        start.toISOString(),
        end.toISOString()
      )
      
      setToastMessage(getTranslation('gpwebpay.export_started', { exportId: response.exportId }))
      setToastError(false)
      
      // Refresh the list and reset to first page
      setCurrentPage(1)
      loadExports()
    } catch (error) {
      console.error('Export failed:', error)
      setToastMessage(getTranslation('gpwebpay.export_failed'))
      setToastError(true)
    } finally {
      setIsExporting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'processing':
        return <Badge status="info">{getTranslation('gpwebpay.export_processing')}</Badge>
      case 'completed':
        return <Badge status="success">{getTranslation('gpwebpay.completed')}</Badge>
      case 'failed':
        return <Badge status="critical">{getTranslation('gpwebpay.failed')}</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  // Calculate pagination
  const totalItems = exports.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedExports = exports.slice(startIndex, endIndex)

  const exportRows = paginatedExports.map((exportItem) => [
    exportItem.id.split('_').pop() || exportItem.id, // Show just the unique part
    formatDate(exportItem.createdAt),
    getStatusBadge(exportItem.status),
    exportItem.totalRecords?.toString() || '-',
    exportItem.status === 'completed' && exportItem.downloadUrl ? (
      <Link url={exportItem.downloadUrl} external>
        Download CSV
      </Link>
    ) : exportItem.status === 'failed' ? (
      <TextStyle variation="subdued">-</TextStyle>
    ) : (
      <Stack alignment="center">
        <Spinner size="small" />
        <TextStyle variation="subdued">{getTranslation('gpwebpay.export_processing')}</TextStyle>
      </Stack>
    ),
  ])

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={getTranslation('gpwebpay.csv_dialog_title')}
        primaryAction={{
          content: getTranslation('gpwebpay.start_export'),
          onAction: handleExport,
          loading: isExporting,
          disabled: !startDate || !endDate || isExporting,
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: onClose,
          },
        ]}
        large
      >
        <Modal.Section>
          <Card sectioned>
            <Form onSubmit={handleExport}>
              <FormLayout>
                <Stack distribution="equalSpacing">
                  <TextField
                    label={getTranslation('gpwebpay.start_date')}
                    value={startDate}
                    onChange={setStartDate}
                    type="date"
                    helpText={getTranslation('gpwebpay.start_date_help')}
                    autoComplete="off"
                  />
                  <TextField
                    label={getTranslation('gpwebpay.end_date')}
                    value={endDate}
                    onChange={setEndDate}
                    type="date"
                    helpText={getTranslation('gpwebpay.end_date_help')}
                    autoComplete="off"
                  />
                </Stack>
                <Caption>
                  {getTranslation('gpwebpay.csv_format_description')}
                </Caption>
              </FormLayout>
            </Form>
          </Card>
        </Modal.Section>

        <Modal.Section>
          <Card title={getTranslation('gpwebpay.recent_exports')} sectioned>
            <Stack vertical spacing="extraLoose">
              {loading ? (
                <Stack alignment="center">
                  <Spinner size="large" />
                  <TextStyle variation="subdued">{getTranslation('gpwebpay.processing')}</TextStyle>
                </Stack>
              ) : exports.length > 0 ? (
                <Stack vertical spacing="loose">
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'text']}
                    headings={[
                      getTranslation('gpwebpay.export_id'),
                      getTranslation('gpwebpay.created'),
                      getTranslation('gpwebpay.status'),
                      getTranslation('gpwebpay.records'),
                      getTranslation('gpwebpay.action')
                    ]}
                    rows={exportRows}
                  />
                  {totalPages > 1 && (
                    <Stack distribution="center">
                      <Pagination
                        hasPrevious={currentPage > 1}
                        onPrevious={() => setCurrentPage(currentPage - 1)}
                        hasNext={currentPage < totalPages}
                        onNext={() => setCurrentPage(currentPage + 1)}
                        label={`Page ${currentPage} of ${totalPages} (${totalItems} exports)`}
                      />
                    </Stack>
                  )}
                </Stack>
              ) : (
                <TextStyle variation="subdued">{getTranslation('gpwebpay.no_exports_found')}</TextStyle>
              )}
              <Caption>
                {getTranslation('gpwebpay.export_processing_description')}
              </Caption>
            </Stack>
          </Card>
        </Modal.Section>
      </Modal>

      {toastMessage && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setToastMessage('')}
        />
      )}
    </>
  )
}
